import { zodResolver } from '@hookform/resolvers/zod';
import { useCreateTenant, useGetTenant, useListTenants } from '@sunstone/api-client/hooks';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@sunstone/ui';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';
import { PageHeader } from '@/components/ui/PageHeader';
import { SearchableCombobox } from '@/components/ui/SearchableCombobox';
import { useAuthToken } from '@/features/auth';
import { useAuthorization } from '@/features/auth/useAuthorization';
import { useDebounce } from '@/hooks/useDebounce';
import { useSlugSync } from '@/hooks/useSlugSync';
import { routes } from '@/lib/routes';
import { displayNameSchema, slugSchema } from '@/lib/schemas';

const schema = z.object({
  name: displayNameSchema,
  slug: slugSchema,
  partnerId: z.string().min(1, 'Select a partner'),
  costCentre: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

export function NewCustomerPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedPartnerId = searchParams.get('partnerId') ?? '';
  const { token } = useAuthToken();
  const { scope, tenantId: authTenantId, hasPermission } = useAuthorization();

  // Partner-scoped users can only create under their own tenant (the partner).
  // Resolve the effective partner ID: URL param takes priority, then the user's own tenantId.
  const isPartnerScoped = scope === 'partner';
  const resolvedPartnerId = preselectedPartnerId || (isPartnerScoped ? authTenantId : '');

  const createTenant = useCreateTenant(token);

  const [partnerSearch, setPartnerSearch] = useState('');
  const debouncedPartnerSearch = useDebounce(partnerSearch);

  const { data: preselectedData } = useGetTenant(resolvedPartnerId, token);
  const preselectedPartnerName = preselectedData?.tenant?.displayName;

  const { data: partnersData } = useListTenants(
    token,
    undefined,
    { search: debouncedPartnerSearch, typeFilter: 'partner', pageSize: 20 },
    !resolvedPartnerId,
  );
  const partners = partnersData?.tenants ?? [];

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      slug: '',
      partnerId: resolvedPartnerId,
      costCentre: '',
    },
  });

  const syncSlug = useSlugSync(form);

  function onSubmit(values: FormValues) {
    createTenant.mutate(
      {
        displayName: values.name.trim(),
        slug: values.slug,
        parentId: values.partnerId,
        costCentre: values.costCentre ?? '',
      },
      {
        onSuccess: (data) => {
          navigate(data.tenant?.id ? routes.tenant(data.tenant.id) : routes.customers());
        },
      },
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <PageHeader
        title="New Customer"
        description={
          resolvedPartnerId && (preselectedPartnerName ?? resolvedPartnerId)
            ? `Creating a customer under ${preselectedPartnerName ?? resolvedPartnerId}.`
            : 'Create a new customer under an existing partner.'
        }
      />

      <Card className="max-w-xl">
        <CardContent className="py-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              {createTenant.isError && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {createTenant.error?.message ?? 'Failed to create customer.'}
                  </AlertDescription>
                </Alert>
              )}

              <FormField
                control={form.control}
                name="partnerId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Partner <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      {resolvedPartnerId ? (
                        <Input value={preselectedPartnerName ?? resolvedPartnerId} disabled />
                      ) : (
                        <SearchableCombobox
                          value={field.value}
                          onChange={(val) => {
                            field.onChange(val);
                            setPartnerSearch('');
                          }}
                          items={partners}
                          search={partnerSearch}
                          onSearchChange={setPartnerSearch}
                          placeholder="Select a partner…"
                          searchPlaceholder="Search partners…"
                          emptyMessage="No partners found."
                          autoOpen
                          autoOpenDelay={600}
                        />
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Customer name <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="e.g. Allstate Claims"
                        autoFocus={!!resolvedPartnerId}
                        onChange={(e) => syncSlug(e.target.value, field.onChange)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="slug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      URL Name <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="allstate-claims" />
                    </FormControl>
                    <FormDescription>Used in URLs and API references.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="costCentre"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cost centre</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. CC-1234" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button type="button" variant="ghost" size="sm" onClick={() => navigate(-1)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={
                    !form.formState.isValid ||
                    createTenant.isPending ||
                    !hasPermission('create:tenant')
                  }
                >
                  {createTenant.isPending ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Creating…
                    </>
                  ) : (
                    'Create Customer'
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
